import React from 'react';
import { Link } from 'react-router-dom';

const PublicationFee = () => {
  return (
    <div className="container py-4">
      <div className="row mb-5">
        <div className="col-lg-8 mx-auto">
          <h1 className="mb-4 text-center">Publication Fee Information</h1>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Open Access Publication Model</h2>
              <p className="card-text">
                FUWJALS operates on an open access model where all published articles are freely 
                available to readers worldwide. To maintain this service and cover the costs of 
                rigorous peer review and professional publishing, we charge Article Processing 
                Charges (APCs) to authors upon manuscript acceptance.
              </p>
              
              <div className="alert alert-info">
                <h4><i className="fas fa-unlock me-2"></i>Why We Charge APCs</h4>
                <p className="mb-0">
                  Our publication fees ensure sustainable, high-quality publishing while keeping 
                  research freely accessible to the global scientific community without subscription barriers.
                </p>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Standard Publication Fees</h2>
              <div className="row">
                <div className="col-lg-8 mx-auto">
                  <div className="card border-primary">
                    <div className="card-header bg-primary text-white text-center">
                      <h3 className="mb-0">Current APC Rate</h3>
                    </div>
                    <div className="card-body text-center">
                      <h1 className="display-4 text-primary">$200 USD</h1>
                      <p className="lead">Per accepted article</p>
                      <hr />
                      <ul className="list-unstyled">
                        <li><i className="fas fa-check text-success me-2"></i>Comprehensive peer review</li>
                        <li><i className="fas fa-check text-success me-2"></i>Professional editing and formatting</li>
                        <li><i className="fas fa-check text-success me-2"></i>DOI assignment and metadata</li>
                        <li><i className="fas fa-check text-success me-2"></i>Global indexing and discovery</li>
                        <li><i className="fas fa-check text-success me-2"></i>Permanent online archiving</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Fee Breakdown by Article Type</h2>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>Article Type</th>
                      <th>Standard Fee</th>
                      <th>Word Limit</th>
                      <th>Typical Processing Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="table-primary">
                      <td><strong>Research Articles</strong></td>
                      <td><strong>$200</strong></td>
                      <td>6,000 words</td>
                      <td>6-8 weeks</td>
                    </tr>
                    <tr className="table-success">
                      <td><strong>Review Articles</strong></td>
                      <td><strong>$250</strong></td>
                      <td>8,000 words</td>
                      <td>8-10 weeks</td>
                    </tr>
                    <tr className="table-warning">
                      <td><strong>Short Communications</strong></td>
                      <td><strong>$150</strong></td>
                      <td>2,000 words</td>
                      <td>4-6 weeks</td>
                    </tr>
                    <tr className="table-info">
                      <td><strong>Technical Notes</strong></td>
                      <td><strong>$125</strong></td>
                      <td>1,500 words</td>
                      <td>4-5 weeks</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Discounted Rates</h2>
              <div className="row">
                <div className="col-md-6">
                  <div className="card border-success h-100">
                    <div className="card-header bg-success text-white">
                      <h4><i className="fas fa-globe-africa me-2"></i>Developing Countries</h4>
                    </div>
                    <div className="card-body">
                      <h3 className="text-success">$150 USD</h3>
                      <p className="card-text">25% discount for authors from:</p>
                      <ul className="small">
                        <li>World Bank lower-middle-income countries</li>
                        <li>Nigeria, Ghana, Kenya, Bangladesh</li>
                        <li>India, Pakistan, Egypt, Morocco</li>
                        <li>50+ other qualifying nations</li>
                      </ul>
                      <div className="alert alert-success">
                        <small><strong>Automatic:</strong> No application required</small>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card border-warning h-100">
                    <div className="card-header bg-warning text-dark">
                      <h4><i className="fas fa-graduation-cap me-2"></i>Student Authors</h4>
                    </div>
                    <div className="card-body">
                      <h3 className="text-warning">$100 USD</h3>
                      <p className="card-text">50% discount for students with:</p>
                      <ul className="small">
                        <li>Current enrollment verification</li>
                        <li>Faculty supervisor co-authorship</li>
                        <li>Valid student ID or letter</li>
                        <li>Institutional email address</li>
                      </ul>
                      <div className="alert alert-warning">
                        <small><strong>Requires:</strong> Documentation submission</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Complete Fee Waivers</h2>
              <div className="alert alert-success">
                <h4><i className="fas fa-gift me-2"></i>100% Fee Waiver Available</h4>
                <p className="mb-0">
                  FUWJALS supports global research accessibility through comprehensive waiver programs 
                  for qualifying authors and institutions.
                </p>
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <h4>Automatic Waivers</h4>
                  <ul>
                    <li><strong>Least Developed Countries (LDCs):</strong> UN-classified nations</li>
                    <li><strong>Invited Articles:</strong> Editorial board solicitations</li>
                    <li><strong>Institutional Partners:</strong> Formal collaboration agreements</li>
                    <li><strong>Editorial Board:</strong> Members and their students</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Application-Based Waivers</h4>
                  <ul>
                    <li><strong>Financial Hardship:</strong> Case-by-case evaluation</li>
                    <li><strong>Research Merit:</strong> Exceptional scientific contribution</li>
                    <li><strong>Special Circumstances:</strong> Natural disasters, conflicts</li>
                    <li><strong>Humanitarian Research:</strong> Public health emergencies</li>
                  </ul>
                </div>
              </div>
              
              <div className="text-center mt-3">
                <a href="mailto:fee-waiver@fuwukari.edu.ng" className="btn btn-success">
                  <i className="fas fa-envelope me-2"></i>Apply for Fee Waiver
                </a>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Value Proposition</h2>
              <h4>What You Get for Your Investment</h4>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="card border-primary mb-3">
                    <div className="card-body">
                      <h5><i className="fas fa-search me-2 text-primary"></i>Quality Assurance</h5>
                      <ul className="small mb-0">
                        <li>Rigorous double-blind peer review</li>
                        <li>Expert editorial oversight</li>
                        <li>Plagiarism detection screening</li>
                        <li>Statistical review when needed</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card border-success mb-3">
                    <div className="card-body">
                      <h5><i className="fas fa-globe me-2 text-success"></i>Global Reach</h5>
                      <ul className="small mb-0">
                        <li>Worldwide open access availability</li>
                        <li>Multiple indexing databases</li>
                        <li>Social media promotion</li>
                        <li>Academic network distribution</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card border-warning mb-3">
                    <div className="card-body">
                      <h5><i className="fas fa-tools me-2 text-warning"></i>Professional Services</h5>
                      <ul className="small mb-0">
                        <li>Professional copy-editing</li>
                        <li>XML and PDF formatting</li>
                        <li>DOI assignment</li>
                        <li>Metadata optimization</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card border-info mb-3">
                    <div className="card-body">
                      <h5><i className="fas fa-shield-alt me-2 text-info"></i>Long-term Preservation</h5>
                      <ul className="small mb-0">
                        <li>Permanent digital archiving</li>
                        <li>LOCKSS preservation network</li>
                        <li>Institutional repository deposits</li>
                        <li>Continuous access guarantee</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Payment Process</h2>
              <div className="row">
                <div className="col-md-8 mx-auto">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h4 className="text-center mb-4">Simple Payment Steps</h4>
                      
                      <div className="d-flex align-items-center mb-3">
                        <span className="badge bg-primary rounded-pill me-3" style={{width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>1</span>
                        <div>
                          <strong>Article Accepted:</strong> Receive acceptance notification with fee invoice
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center mb-3">
                        <span className="badge bg-success rounded-pill me-3" style={{width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>2</span>
                        <div>
                          <strong>Choose Payment Method:</strong> Select from multiple convenient options
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center mb-3">
                        <span className="badge bg-warning rounded-pill me-3" style={{width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>3</span>
                        <div>
                          <strong>Complete Payment:</strong> Process payment through secure gateway
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center mb-3">
                        <span className="badge bg-info rounded-pill me-3" style={{width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>4</span>
                        <div>
                          <strong>Confirmation:</strong> Submit payment proof and receive verification
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center">
                        <span className="badge bg-dark rounded-pill me-3" style={{width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>5</span>
                        <div>
                          <strong>Publication:</strong> Article published online within 1-2 weeks
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="alert alert-info mt-4">
                <h5><i className="fas fa-clock me-2"></i>Payment Deadline</h5>
                <p className="mb-0">
                  Payment must be completed within 30 days of acceptance notification. 
                  Extensions available upon request for legitimate reasons.
                </p>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Comparison with Other Journals</h2>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-secondary">
                    <tr>
                      <th>Publisher Category</th>
                      <th>Average APC (USD)</th>
                      <th>FUWJALS APC</th>
                      <th>Your Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Major Commercial Publishers</td>
                      <td>$3,000 - $5,000</td>
                      <td>$200</td>
                      <td><span className="badge bg-success">Save 90-95%</span></td>
                    </tr>
                    <tr>
                      <td>Society Publishers</td>
                      <td>$1,500 - $2,500</td>
                      <td>$200</td>
                      <td><span className="badge bg-success">Save 85-90%</span></td>
                    </tr>
                    <tr>
                      <td>Regional Open Access</td>
                      <td>$500 - $1,200</td>
                      <td>$200</td>
                      <td><span className="badge bg-success">Save 60-80%</span></td>
                    </tr>
                    <tr>
                      <td>Predatory Publishers</td>
                      <td>$50 - $500</td>
                      <td>$200</td>
                      <td><span className="badge bg-warning">Quality Guaranteed</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="alert alert-success">
                <h5><i className="fas fa-medal me-2"></i>Best Value Proposition</h5>
                <p className="mb-0">
                  FUWJALS offers the quality standards of top-tier journals at a fraction 
                  of the cost, making high-quality publishing accessible to all researchers.
                </p>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Frequently Asked Questions</h2>
              
              <div className="accordion" id="faqAccordion">
                <div className="accordion-item">
                  <h5 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                      When is the publication fee due?
                    </button>
                  </h5>
                  <div id="faq1" className="accordion-collapse collapse">
                    <div className="accordion-body">
                      Publication fees are due only after manuscript acceptance, not during submission or review. 
                      You have 30 days from acceptance to complete payment.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item">
                  <h5 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                      Are there any hidden costs?
                    </button>
                  </h5>
                  <div id="faq2" className="accordion-collapse collapse">
                    <div className="accordion-body">
                      No hidden costs. The APC covers all standard editorial and publishing services. 
                      Any optional services (like expedited processing) are clearly communicated.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item">
                  <h5 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                      Can I get a refund if I change my mind?
                    </button>
                  </h5>
                  <div id="faq3" className="accordion-collapse collapse">
                    <div className="accordion-body">
                      Publication fees are generally non-refundable as they cover services already provided. 
                      Refunds may be considered in exceptional circumstances like duplicate payments.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Contact Us</h2>
              <div className="row">
                <div className="col-md-6">
                  <h5>Publication Fees</h5>
                  <p>
                    <i className="fas fa-envelope me-2"></i>Email: fees@fuwukari.edu.ng<br />
                    <i className="fas fa-phone me-2"></i>Phone: +234 XXX XXX XXXX<br />
                    <i className="fas fa-clock me-2"></i>Response: Within 24 hours
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Fee Waivers & Discounts</h5>
                  <p>
                    <i className="fas fa-envelope me-2"></i>Email: fee-waiver@fuwukari.edu.ng<br />
                    <i className="fas fa-calendar me-2"></i>Processing: 5-7 business days<br />
                    <i className="fas fa-info-circle me-2"></i>Decision: Email notification
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/submit-paper" className="btn btn-primary btn-lg me-3">
              <i className="fas fa-paper-plane me-2"></i>Submit Your Article
            </Link>
            <Link to="/payment-methods" className="btn btn-outline-secondary">
              <i className="fas fa-credit-card me-2"></i>Payment Options
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicationFee;